/**
 * A heading row: a title, and optionally a count beside it.
 *
 * WHY THIS EXISTS, AND WHY IT IS THIS SMALL. Four places had grown the same
 * heading independently. Three pairs of rules were byte-identical
 * (job-list's `.header`/`.title`/`.count` and the users page's
 * `.tableHeader`/`.title`/`.count`), and the dashboard's had already drifted
 * from both while doing the same job. Duplication nobody notices is
 * duplication that diverges.
 *
 * WHAT IT DELIBERATELY DOES NOT ABSORB. The dashboard heading is an h1 at
 * --text-h2 with display tracking and a loading spinner beside it; the
 * calendar's sits inside the breadcrumb bar rather than in the page body; the
 * job-details "header" is a two-card hero and shares nothing but a name. A
 * component that swallowed all of those would need a prop per caller, and a
 * shared component configured five ways is worse than the duplication it
 * replaced. Those stay as they are, and this covers what actually repeats.
 */

import { memo, ReactNode } from 'react';
import styles from './index.module.css';

export interface CountLabel {
  value: number;
  /** Noun for one, e.g. "job". */
  singular: string;
  /** Noun for none or many, e.g. "jobs". */
  plural: string;
}

interface Props {
  title: string;
  /**
   * Rendered beside the title. Pluralised against the value, and formatted
   * with separators -- a four-figure count reading "1234 candidates" looks
   * like an id rather than a quantity.
   */
  count?: CountLabel;
  /**
   * 'page' is the heading for the page itself. 'section' steps down for a
   * heading that sits under an existing page title, so the two do not compete.
   */
  level?: 'page' | 'section';
  /** Anything that belongs on the row after the count -- a spinner, a link. */
  children?: ReactNode;
}

const PageHeading = ({ title, count, level = 'page', children }: Props) => {
  const Tag = level === 'page' ? 'h1' : 'h2';
  return (
    <div className={styles.row}>
      <Tag className={level === 'page' ? styles.title : styles.titleSub}>
        {title}
      </Tag>
      {/* `count` alone being truthy does not mean `count.value` is a number --
          a caller can pass a literal object wrapping an as-yet-unloaded
          total, e.g. `{{ value: totalElements, ... }}` before that state has
          resolved. Checked separately so an undefined value renders nothing
          rather than throwing on `.toLocaleString()`. */}
      {count && typeof count.value === 'number' && (
        <span className={styles.count}>
          {count.value.toLocaleString('en-US')}{' '}
          {count.value === 1 ? count.singular : count.plural}
        </span>
      )}
      {children}
    </div>
  );
};

export default memo(PageHeading);
